import React, { useState, useEffect } from 'react';
import { Button, Typography, Spin } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const VerifyEmail = () => {
  const [searchParams]          = useSearchParams();
  const token                   = searchParams.get('token');
  const navigate                = useNavigate();

  const [status, setStatus]     = useState('loading'); // 'loading' | 'success' | 'error' | 'pending'
  const [message, setMessage]   = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('pending');
      return;
    }
    axios.get(`/api/auth/verify-email?token=${token}`)
      .then(res => {
        // Update token in localStorage if the server returned a new one
        if (res.data.token) localStorage.setItem('token', res.data.token);
        setStatus('success');
      })
      .catch(err => {
        setMessage(err.response?.data?.message || 'Verification failed');
        setStatus('error');
      });
  }, [token]);

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
    padding: '24px',
  };

  const cardStyle = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '52px 44px',
    maxWidth: 440,
    width: '100%',
    textAlign: 'center',
  };

  const iconCircle = (bg, icon) => (
    <div style={{
      width: 80, height: 80,
      background: bg,
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 24,
    }}>
      {icon}
    </div>
  );

  if (status === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            <Text style={{ color: 'rgba(255,255,255,0.6)' }}>Verifying your email…</Text>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          {iconCircle('rgba(16,185,129,0.15)', <CheckCircleOutlined style={{ fontSize: 40, color: '#10b981' }} />)}
          <Title level={3} style={{ color: '#fff', margin: '0 0 8px' }}>Email verified!</Title>
          <Text style={{ color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 32 }}>
            Your account is now fully activated. You're all set to start sending campaigns.
          </Text>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/')}
            style={{ borderRadius: 10, height: 46, fontWeight: 600, paddingInline: 32 }}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          {iconCircle('rgba(239,68,68,0.15)', <CloseCircleOutlined style={{ fontSize: 40, color: '#ef4444' }} />)}
          <Title level={3} style={{ color: '#fff', margin: '0 0 8px' }}>Link invalid or expired</Title>
          <Text style={{ color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 8 }}>
            {message || 'This verification link may have expired or already been used.'}
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, display: 'block', marginBottom: 32 }}>
            Log in and request a new verification email from your settings.
          </Text>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/login')}
            style={{ borderRadius: 10, height: 46, fontWeight: 600, paddingInline: 32 }}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // status === 'pending' — no token in URL, show "check your inbox"
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {iconCircle('rgba(99,102,241,0.2)', <MailOutlined style={{ fontSize: 40, color: '#a5b4fc' }} />)}
        <Title level={3} style={{ color: '#fff', margin: '0 0 8px' }}>Check your inbox</Title>
        <Text style={{ color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 12 }}>
          We sent a verification link to your email address. Click it to activate your account.
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 13, display: 'block', marginBottom: 32 }}>
          Didn't receive it? Check your spam folder, or wait a minute and try again.
        </Text>
        <Button
          onClick={() => navigate('/login')}
          style={{
            borderRadius: 10, height: 44, fontWeight: 500,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff',
          }}
        >
          Back to Login
        </Button>
      </div>
    </div>
  );
};

export default VerifyEmail;
